import { Component, Input } from "@angular/core";
import {
  ModalController,
  NavParams,
  PopoverController,
  ToastController,
} from "ionic-angular";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subscription } from "rxjs";
import { AuthService } from "../../../../app/auth/auth.service";
import { EditViewPopoverPage } from "../../../../navigation/components/edit-view-popover/edit-view-popover";
import { MarDataServiceProvider } from "../../../../providers/mar-data-service/mar-data-service";
import { MobileUtilsProvider } from "../../../../providers/mobile-utils/mobile-utils";
import { OrderDataService } from "../../../../providers/order-data-service/order-data-service";
import { OrderManagementDataService } from "../../../../providers/order-management-data-service/order-management-data-service";
import { ReportExecutionService } from "../../../../providers/report-execution-service/report-execution.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { MAR_CARD_CONFIG } from "./mar-card-config";

@Component({
  selector: "mar-card",
  templateUrl: "mar-card.html",
})
export class MarCardComponent {
  @Input() patChartId: string;
  filterDate: string;
  /** Model behind the "Filter:" date picker. The template binds
   *  [(ngModel)]="dateValue"; without this declaration the picker opened with an
   *  EMPTY field while the grid below was already loaded for today. */
  dateValue: string;
  marMaxDate: object;
  marGrid = MAR_CARD_CONFIG["marGrid"];
  marMeds: Array<object> = [];
  isMobilePlatform: boolean = false;
  orderEntryToggle: boolean = false;
  private getMARMesSubscription$: Subscription;

  timeslotLookup = new Map([
    ["12am", "0000"],
    ["1am", "0100"],
    ["2am", "0200"],
    ["3am", "0300"],
    ["4am", "0400"],
    ["5am", "0500"],
    ["6am", "0600"],
    ["7am", "0700"],
    ["8am", "0800"],
    ["9am", "0900"],
    ["10am", "1000"],
    ["11am", "1100"],
    ["12pm", "1200"],
    ["1pm", "1300"],
    ["2pm", "1400"],
    ["3pm", "1500"],
    ["4pm", "1600"],
    ["5pm", "1700"],
    ["6pm", "1800"],
    ["7pm", "1900"],
    ["8pm", "2000"],
    ["9pm", "2100"],
    ["10pm", "2200"],
    ["11pm", "2300"],
  ]);
  constructor(
    private marDataService: MarDataServiceProvider,
    private dataService: OrderDataService,
    private utilityService: UtilityService,
    private state: StateManagementService,
    public navParams: NavParams,
    public modalCtrl: ModalController,
    private mobileUtilsProvider: MobileUtilsProvider,
    private popoverCtrl: PopoverController,
    private reportExecutionService: ReportExecutionService,
    private orderManagementDataService: OrderManagementDataService,
    private toastCtrl: ToastController,
    private authSvc: AuthService
  ) {
    this.patChartId = this.navParams.get("patChartId");
    this.filterDate = moment();
    this.dateValue = moment().toString();
    this.marMaxDate = this.getDateObj(moment());
    this.getMARMedications(this.patChartId);
    this.isMobilePlatform = mobileUtilsProvider.isMobilePlatform();
  }

  getMARMedications(patChartId: string) {
    this.dataService.increaseSpinnerCount();
    let utcFilterDates = this.getFilterUTCDates();
    this.getMARMesSubscription$ = this.marDataService
      .getMARMedications(utcFilterDates, patChartId)
      .subscribe(
        (data) => {
          this.dataService.decreaseSpinnerCount();

          // separate active/discontinued meds
          let discontinuedMeds = data.filter(
            (meds) => meds.orderStatus === "DI" || meds.orderStatus === "SU"
          );
          let activeMeds = data.filter(
            (meds) => meds.orderStatus !== "DI" && meds.orderStatus !== "SU"
          );

          // sort meds alphabetically
          //this.marMeds = _.sortBy(activeMeds, ['medication.name', 'itemStartDateTime']);
          this.marMeds = _.sortBy(activeMeds, [
            (activeMed) => activeMed.medication.name.toLowerCase(),
            "itemStartDateTime",
          ]);
          discontinuedMeds = _.sortBy(discontinuedMeds, [
            (discontinuedMed) => discontinuedMed.medication.name.toLowerCase(),
            "itemStartDateTime",
          ]);

          // add discontinued meds to the end of the list to display discontinued meds AFTER active meds
          this.marMeds = this.marMeds.concat(discontinuedMeds);
          if (this.marMeds.length) {
            this.orderManagementDataService.getOrderTypes().subscribe(
              (orderTypeConfigurations) => {
                const marFrequencyComponent = this.findInNestedArray(
                  orderTypeConfigurations,
                  "name",
                  "MARFrequency"
                );
                // A configuration with no MARFrequency component is not an
                // error — it just means no schedule labels. Dereferencing the
                // null here used to throw INSIDE this success callback, which
                // skipped the error branch below and left med.marGrid unset, so
                // the grid rendered with no slots at all.
                this.getDataForGrid(
                  this.marMeds,
                  (marFrequencyComponent && marFrequencyComponent.dropdownItems) || []
                );
              },
              () => {
                this.toastCtrl
                  .create({
                    message:
                      "Could not get a list of administration frequencies.",
                    duration: 6000,
                    position: "below",
                    cssClass: "error",
                  })
                  .present();
                this.getDataForGrid(this.marMeds, []);
              }
            );
          }
        },
        (err) => {
          this.dataService.decreaseSpinnerCount();
          // Drop the previous day's rows. Leaving them up after a failed reload
          // showed one day's medications under another day's date filter.
          this.marMeds = [];
        }
      );
  }

  private findInNestedArray(array, propertyName, value) {
    function searchNested(items) {
      for (const item of items) {
        if (item[propertyName] === value) {
          return item;
        }
        for (const key in item) {
          if (Array.isArray(item[key])) {
            const result = searchNested(item[key]);
            if (result) return result;
          }
        }
      }
      return null;
    }
    return searchNested(array);
  }

  ngOnInit() {
    this.state.showOrderEntryOM.subscribe((x) => {
      if (x !== null) {
        this.orderEntryToggle = x;
      }
    });
  }

  getDataForGrid(medicationArray, frequencyList) {
    /* This is a first run-through of each medication to determine which time slots of each grid need to be marked as upcoming or late */
    if (medicationArray) {
      medicationArray.forEach((med) => {
        let marData = _.cloneDeep(this.marGrid);
        let currDateTime = new moment().format("YYYY-MM-DD HH:mm:ss");

        // formatted filterDate (the date we're showing the MAR grid for)
        let filterDateTime = moment(this.filterDate)
          .set("hour", moment(currDateTime).get("hour"))
          .set("minute", moment(currDateTime).get("minute"))
          .set("second", moment(currDateTime).get("second"))
          .format("YYYY-MM-DD HH:mm:ss");

        if (
          med.medication.prescribedSchedule &&
          med.medication.prescribedSchedule !== "NA"
        ) {
          const frequencyItem = frequencyList.find(
            (f) => f.value.toLowerCase() == med.medication.prescribedSchedule
          );
          if (frequencyItem) {
            med.medication.prescribedScheduleText = frequencyItem.label;
          }
        }
        if (med.medication.prescribedTimes) {
          marData.map((slots, row) => {
            med.medication.prescribedTimes.map((time) => {
              let prescribedTimeCol = _.findIndex(slots, [
                "text",
                moment(time, "hh").format("ha"),
              ]);
              if (marData[row][prescribedTimeCol]) {
                let slotTime = moment(time, "hh").format("YYYY-MM-DD HH:mm:ss");

                // formatted slot time
                let slotDateTime = moment(filterDateTime)
                  .set("hour", moment(slotTime).get("hour"))
                  .set("minute", moment(slotTime).get("minute"))
                  .set("second", moment(slotTime).get("second"))
                  .format("YYYY-MM-DD HH:mm:ss");

                // add 1h buffer to slot time
                let slotDeadline = moment(slotDateTime)
                  .add(1, "h")
                  .format("YYYY-MM-DD HH:mm:ss");

                if (
                  (filterDateTime > slotDeadline ||
                    currDateTime > filterDateTime) &&
                  marData[row][prescribedTimeCol].slotType === "empty"
                ) {
                  marData[row][prescribedTimeCol].slotType = "late-missed-slot";
                } else {
                  marData[row][prescribedTimeCol].slotType = "schedule-slot";
                }
              }
            });
          });
        }

        // Discontinued Med logic - grey out slots after discontinueDateTime
        if (med.orderStatus === "DI" || med.orderStatus === "SU") {
          let discontinueDateTime = moment
            .utc(med.itemDiscontinueDateTime, "YYYY/MM/DD HH:mm:ss")
            .local()
            .format("YYYY-MM-DD HH:mm:ss");
          let formattedFilterDate = moment(this.filterDate).format(
            "YYYY-MM-DD"
          );
          let discontinueDate =
            moment(discontinueDateTime).format("YYYY-MM-DD");

          // if the filterDate selected is past discontinueDate, grey out ALL slots for the med
          if (formattedFilterDate > discontinueDate) {
            marData.forEach((gridRow) => {
              gridRow.forEach((slot) => {
                slot.slotType = "disabled-slot";
              });
            });
          }
          // if selected filterDate = discontinueDate...
          // ...we don't want to grey out all slots, instead only grey out slots AFTER discontinueDateTime hour slot
          else if (formattedFilterDate == discontinueDate) {
            let discontinueTime = moment(discontinueDateTime).get("hour");
            let discontinueRowIndex: number = null;
            let discontinueColIndex: number = null;

            // Iterate through the med grid to find the discontinue hour slot, then grey out slots accordingly.
            marData.map((slots, row) => {
              // If the discontinue slot has been identified...
              if (
                discontinueColIndex !== null &&
                discontinueColIndex >= 0 &&
                discontinueRowIndex !== null &&
                discontinueRowIndex >= 0
              ) {
                //...and if current row is past the discontinue slot row..
                if (row > discontinueRowIndex) {
                  let gridRow = marData[row];
                  // ...grey out ALL slots in that row.
                  gridRow.forEach((slot) => {
                    slot.slotType = "disabled-slot";
                  });
                }
              }
              // Have not yet found the slot position where the 'grey out' needs to begin...
              else {
                // ...search through the set of slots in the current grid row
                discontinueColIndex = _.findIndex(slots, [
                  "text",
                  moment(discontinueTime, "hh").format("ha"),
                ]);

                // found the slot position where grey out needs to happen?
                if (marData[row][discontinueColIndex]) {
                  discontinueRowIndex = row;
                  let gridRow = marData[row];

                  // grey out all slots at/after discontinue hour slot
                  for (let i = 0; i < gridRow.length; i++) {
                    if (i >= discontinueColIndex) {
                      gridRow[i].slotType = "disabled-slot";
                    }
                  }
                }
              }
            });
          }
        }

        /* Now we're going to iterate through medication.medicationAdminRecords[] to determine:
         * - which slot to display each given dosage in (based on medicationAdminRecords[n].prescribedSlot)
         * - whether to display the dosage time as black or red text on the slot (based on medicationAdminRecords[n].administeredFlag)
         * - whether to display the upper-right slot time in a red background (if medicationAdminRecords[n].administeredFlag == false) */
        if (!_.isEmpty(med.medication.medicationAdminRecords)) {
          med.medication.medicationAdminRecords.forEach((mar) => {
            if (mar["prescribedSlot"]) {
              let timeSlot = mar["prescribedSlot"]; // timeSlot = prescribedSlot from API response
              mar.relativeTimeSlot = this.getRelativeTimeSlot(
                med.medication,
                mar
              );
              marData = this.getTimeSlot(marData, timeSlot, mar); // this is where we can mark a slot as late-missed-slot
            }
          });
          med.medication.medicationAdminRecords.forEach((mar) => {
            marData = this.getTimeSlotStyling(marData, mar);
          });
        }
        med.marGrid = marData;
      });
    }
  }

  /**
   * Given a MAR record (param: mar), SET appropriate properties for its associated UI slot in the grid (param: marGrid)
   *
   * @param - mar - is the medication record we are going to either add/remove/update based on mar.actionTaken
   * @param - marGrid - is the grid data structure for a given medication (mar.name)
   * @param - timeSlot - is the mar.prescribedSlot
   */
  getTimeSlot(marGrid, timeSlot, mar) {
    timeSlot = this.getPrescribedTimeSlot(timeSlot);
    marGrid.map((slots, row) => {
      let col = _.findIndex(slots, ["militaryTime", timeSlot]);

      // update the slot's marArray and administeredTimes
      if (marGrid[row][col]) {
        // this is our slot right here on the grid, let's check if it is a valid object before doing anything
        /* [1] - add/update/delete current MAR in the slot's collection of MARs (slot.marArray) */
        this.updateTimeSlotMarArray(mar, marGrid, row, col);

        /* [2] - add/remove current MAR's administeredDateTime to slot.administeredTimes (in the UI, these are the individual dosage times that appear on the slot) */
        this.updateTimeSlotAdministeredTimes(mar, marGrid, row, col);
      }
    });

    return marGrid;
  }

  /* [3] - Update the look and feel of the slot *relative* to the administeredTime. This is going to be one of the med.prescribedTimes slots.
   * SEE: getRelativeTimeSlot() for how this is determined.
   */
  getTimeSlotStyling(marGrid, mar) {
    if (!_.isEmpty(mar.relativeTimeSlot)) {
      marGrid.map((slots, row) => {
        let col = _.findIndex(slots, ["militaryTime", mar.relativeTimeSlot]);
        // Now, let's update styling for the relative slot to administeredDateTime. Either mark it as late and/or 'Completed'/'Not Administered'
        if (marGrid[row][col]) {
          this.updateTimeSlotStyling(mar, marGrid, row, col);
        }
      });
    }
    return marGrid;
  }

  /* Get the slot based on mar.prescribedSlot */
  getRelativeTimeSlot(med, mar) {
    let prescribedTimeSlot = this.getPrescribedTimeSlot(mar.prescribedSlot);
    return prescribedTimeSlot;
  }

  /**
   * Adds the mar to the appropriate time slot so the dosage time is displayed on the appropriate time slot.
   *
   * @param - mar - is the medication record we are going to either add/remove/update based on mar.actionTaken
   * @param - marGrid - is the grid data structure for a given medication (mar.name)
   * @param - row, col - are the indices to locate the slot on marGrid. This slot would then be updated based on the mar.
   */
  updateTimeSlotMarArray(mar, marGrid, row, col) {
    let marArray = marGrid[row][col].marArray;

    switch (mar.actionTaken) {
      case null:
      case undefined:
      case "Created":
      case "created":
        if (_.isEmpty(marArray)) {
          marArray = [mar];
        } else {
          marArray.push(mar);
        }
        break;

      case "Updated":
      case "updated":
        // findIndex returns -1 when the record is not in this slot, and
        // `marArray[-1] = mar` writes a non-index property: the update was
        // dropped while the array quietly grew a "-1" key.
        let index = _.findIndex(marArray, ["id", mar.id]);
        if (index > -1) {
          marArray[index] = mar;
        }
        break;

      case "Deleted":
      case "deleted":
        if (!_.isEmpty(marArray)) {
          if (marArray.length === 1 && marArray[0].id === mar.id) {
            marArray = [];
          } else if (marArray.length > 1) {
            let index = _.findIndex(marArray, ["id", mar.id]);

            if (index > -1) {
              marArray.splice(index, 1);
            }
          }
        }
        break;
    }

    marGrid[row][col].marArray = marArray;
  }

  // mark the dosage time as 'administered' or 'not-administered'
  // setting type as 'administered-time' will display dosage time in normal black color, and setting as 'not-administered-time' will display time in red
  updateTimeSlotAdministeredTimes(mar, marGrid, row, col) {
    let slotAdministeredTimes = marGrid[row][col].administeredTimes;
    let marAdministeredTimeMilitary = moment(
      this.utilityService.covertDatetoLocalFormat(mar.administeredDateTime)
    ).format("HHmm");

    switch (mar.actionTaken) {
      case null:
      case undefined:
      case "Created":
      case "created":
        if (mar.administeredDateTime) {
          let administeredTimes = [
            {
              militaryTime: marAdministeredTimeMilitary,
              type: mar.administeredFlag
                ? "administered-time"
                : "not-administered-time",
            },
          ];

          if (_.isEmpty(slotAdministeredTimes)) {
            slotAdministeredTimes = administeredTimes;
          } else if (slotAdministeredTimes.length < 6) {
            slotAdministeredTimes = _.orderBy(
              slotAdministeredTimes.concat(administeredTimes),
              ["militaryTime"],
              ["asc"]
            );
          } else {
            slotAdministeredTimes = _.orderBy(
              slotAdministeredTimes.concat(administeredTimes),
              ["militaryTime"],
              ["asc"]
            ).slice(0, 6);
          }
        }

        break;

      // do nothing when mar is updated -- (as administeredDateTime cannot be changed for a dosage)
      case "updated":
      case "Updated":
        break;

      case "Deleted":
      case "deleted":
        // find index of marAdministeredTimeMilitary in administeredTimesArray.
        // if exists, remove. If not, do nothing...
        if (!_.isEmpty(slotAdministeredTimes)) {
          let timeIndex = _.findIndex(slotAdministeredTimes, [
            "militaryTime",
            marAdministeredTimeMilitary,
          ]);

          if (
            timeIndex !== null &&
            typeof timeIndex !== "undefined" &&
            timeIndex > -1
          ) {
            slotAdministeredTimes.splice(timeIndex, 1);
          }
        }

        break;

      default:
    }

    marGrid[row][col].administeredTimes = slotAdministeredTimes;
  }

  // Update marGrid[row][col]'s slotType and timeDisplay based on mar.administeredDateTime
  updateTimeSlotStyling(mar, marGrid, row, col) {
    let slotType = marGrid[row][col].slotType;
    let timeDisplay = marGrid[row][col].timeDisplay;

    let timeSlot = mar.relativeTimeSlot;
    let relativeMarArray = marGrid[row][col].relativeMarArray;
    let slotTime = moment(timeSlot, "hh").format("YYYY-MM-DD HH:mm:ss");

    // convert slotTime to slotDateTime
    let slotDateTime = moment(this.filterDate)
      .set("hour", moment(slotTime).get("hour"))
      .set("minute", moment(slotTime).get("minute"))
      .set("second", moment(slotTime).get("second"))
      .format("YYYY-MM-DD HH:mm:ss");

    // calculate deadline for slot (slotTime + 1h)
    let slotDeadline = moment(slotDateTime)
      .add(1, "h")
      .format("YYYY-MM-DD HH:mm:ss");

    switch (mar.actionTaken) {
      case null:
      case undefined:
      case "Created":
      case "created":
        marGrid[row][col].relativeMarArray.push(mar);
        // if(slotType !== 'late-dosage-slot') {
        //   slotType = localAdministeredTime > slotDeadline ? 'late-dosage-slot' : 'on-time-slot';
        // }

        relativeMarArray = _.orderBy(
          relativeMarArray,
          ["administeredDateTime"],
          ["asc"]
        );

        let lastMar = _.isEmpty(relativeMarArray)
          ? mar
          : relativeMarArray[relativeMarArray.length - 1];
        let lastMarAdministeredTime = moment(
          this.utilityService.covertDatetoLocalFormat(
            lastMar.administeredDateTime
          )
        ).format("YYYY-MM-DD HH:mm:ss");

        // set slotType based on the last mar recorded for this slot
        slotType =
          lastMarAdministeredTime > slotDeadline
            ? "late-dosage-slot"
            : "on-time-slot";

        // set timeDisplay — from the SAME record slotType was derived from.
        // This used to read localAdministeredTime (the record being applied),
        // so a slot could come out typed on-time while displaying the late
        // style, and vice versa, whenever the applied record was not the last.
        if (lastMar.administeredFlag) {
          if (lastMar.prn || lastMarAdministeredTime <= slotDeadline) {
            timeDisplay = "administered-slot";
          } else {
            timeDisplay = "administered-late-slot";
          }
        } else if (!lastMar.administeredFlag) {
          timeDisplay = "not-administered-slot";
        }

        break;

      // slot styling is not impacted when a mar is updated as the mar.administeredDateTime is read-only after initial creation.
      case "Updated":
      case "updated":
        break;

      case "Deleted":
      case "deleted":
        // reset slotType
        slotType = "empty";

        if (!_.isEmpty(relativeMarArray)) {
          let timeIndex = _.findIndex(relativeMarArray, ["id", mar.id]);

          if (
            timeIndex !== null &&
            typeof timeIndex !== "undefined" &&
            timeIndex > -1
          ) {
            relativeMarArray.splice(timeIndex, 1);
          }
        }

        relativeMarArray = _.orderBy(
          relativeMarArray,
          ["administeredDateTime"],
          ["asc"]
        );

        lastMar = _.isEmpty(relativeMarArray)
          ? null
          : relativeMarArray[relativeMarArray.length - 1];

        if (lastMar) {
          lastMarAdministeredTime = moment(
            this.utilityService.covertDatetoLocalFormat(
              lastMar.administeredDateTime
            )
          ).format("YYYY-MM-DD HH:mm:ss");
          slotType =
            lastMarAdministeredTime > slotDeadline
              ? "late-dosage-slot"
              : "on-time-slot";

          if (lastMar.administeredFlag) {
            // Same fix as the created branch: style from the record that
            // REMAINS, not the one that was just deleted. Deleting a late dose
            // used to leave the slot still wearing the "administered late"
            // style even though the surviving dose was on time.
            if (lastMar.prn || lastMarAdministeredTime <= slotDeadline) {
              timeDisplay = "administered-slot";
            } else {
              timeDisplay = "administered-late-slot";
            }
          } else if (!lastMar.administeredFlag) {
            timeDisplay = "not-administered-slot";
          }
        } else {
          timeDisplay = "normal";
          let currDateTime = new moment().format("YYYY-MM-DD HH:mm:ss");
          let slotTime = moment(marGrid[row][col].militaryTime, "hh").format(
            "YYYY-MM-DD HH:mm:ss"
          );

          // formatted filterDate (the date we're showing the MAR grid for)
          let filterDateTime = moment(this.filterDate)
            .set("hour", moment(currDateTime).get("hour"))
            .set("minute", moment(currDateTime).get("minute"))
            .set("second", moment(currDateTime).get("second"))
            .format("YYYY-MM-DD HH:mm:ss");

          // formatted slot time
          let slotDateTime = moment(filterDateTime)
            .set("hour", moment(slotTime).get("hour"))
            .set("minute", moment(slotTime).get("minute"))
            .set("second", moment(slotTime).get("second"))
            .format("YYYY-MM-DD HH:mm:ss");

          // add 1h buffer to slot time
          let slotDeadline = moment(slotDateTime)
            .add(1, "h")
            .format("YYYY-MM-DD HH:mm:ss");

          // set slotType
          if (
            (filterDateTime > slotDeadline || currDateTime > filterDateTime) &&
            slotType === "empty"
          ) {
            slotType = "late-missed-slot";
          } else {
            slotType = "schedule-slot";
          }
        }

        break;

      default:
    }

    marGrid[row][col].slotType = slotType;
    marGrid[row][col].timeDisplay = timeDisplay;
  }

  getSlotType() {}

  emitValueChanged(date: any) {
    if (date != null) {
      this.filterDate = date;
      this.getMARMedications(this.patChartId);
    } else {
      this.marMeds = [];
    }
  }

  getDateObj(date) {
    return {
      day: moment(date).date(),
      month: 1 + moment(date).month(),
      year: moment(date).year(),
    };
  }

  onMarGridCell(med, mar, cell) {
    let currDateTime = moment().format("YYYY-MM-DD HH:mm:s");
    let slotTime = moment(cell, "HH a").format("YYYY-MM-DD HH:mm:ss");

    currDateTime = moment(currDateTime)
      .set("hour", moment(currDateTime).get("hour"))
      .set("minute", "00")
      .set("second", "00")
      .format("YYYY-MM-DD HH:mm:ss");

    let slotDateTime = moment(this.filterDate)
      .set("hour", moment(slotTime).get("hour"))
      .set("minute", moment(slotTime).get("minute"))
      .set("second", moment(slotTime).get("second"))
      .format("YYYY-MM-DD HH:mm:ss");

    currDateTime = moment(currDateTime)
      .add(2, "h")
      .format("YYYY-MM-DD HH:mm:s");

    // Get current cell time in militaryTime
    let slotTimeMilitary = this.getPrescribedTimeSlot(cell);

    // Get scheduled/prescribed times array (military Time)
    let prescribedTimesArr = med.medication.prescribedTimes;

    // Popup open restrictions
    // only show popup if the slot clicked is anything less currTime + 1hr AND for schedule slots (i.e 'med.prescribedTimes')...
    if (slotDateTime < currDateTime) {
      // ...further restrictions for Discontinued meds - Only allow open if slot datetime is < discontinued datetime
      if (med.orderStatus === "DI" || med.orderStatus === "SU") {
        let discontinueDateTime = moment
          .utc(med.itemDiscontinueDateTime, "YYYY/MM/DD HH:mm:ss")
          .local()
          .format("YYYY-MM-DD HH:mm:ss");
        if (
          slotDateTime < discontinueDateTime &&
          _.includes(prescribedTimesArr, slotTimeMilitary)
        ) {
          this.openMarPopUp(med, mar, cell, this.filterDate);
        }
      } else if (med.medication.prescribedSchedule === "NA") {
        // for PRN order, allow open on any slot
        this.openMarPopUp(med, mar, cell, this.filterDate);
      } else if (_.includes(prescribedTimesArr, slotTimeMilitary)) {
        // user clicked on one of the 'prescribedTimes' slot?
        this.openMarPopUp(med, mar, cell, this.filterDate);
      }
    }
  }

  openMarPopUp(meds, mar, cell, filterDate) {
    let administrationModal = this.modalCtrl.create(
      "MarAdministrationPage",
      {
        meds: meds,
        mar: mar,
        cell: cell,
        filterDate: filterDate,
      },
      { enableBackdropDismiss: false, cssClass: "mar-administration-modal" }
    );
    administrationModal.present();
    administrationModal.onDidDismiss((data) => {
      if (data) {
        this.createMarRecord(data, meds, cell);
      }
    });
  }

  updateTimeSlot(medId, cell, marResponse) {
    var medIndex = this.marMeds.findIndex(
      (med) => med["medication"].id == medId
    );
    // The medication can be off the grid by the time a write comes back (a
    // reload landed while the modal was open). Dereferencing marMeds[-1] threw
    // here — and because this runs BEFORE the POST, the nurse's entry was lost
    // without any error surfacing.
    if (medIndex < 0) {
      return;
    }
    marResponse.forEach((mar) => {
      mar.relativeTimeSlot = this.getRelativeTimeSlot(
        this.marMeds[medIndex]["medication"],
        mar
      );
      this.getTimeSlot(this.marMeds[medIndex]["marGrid"], cell, mar);
      this.getTimeSlotStyling(this.marMeds[medIndex]["marGrid"], mar);
    });
  }

  createMarRecord(data, meds, cell) {
    let mar = [];
    let formattedFilterDate = moment(this.filterDate).format("YYYY-MM-DD");
    let formattedCell = moment(cell, ["h:mm A"]).format("HH:mm");

    for (let i = 0; i < data.length; i++) {
      let finalAdministeredDateTime = moment(this.filterDate)
        .set("hour", moment(data[i].administeredDateTime).get("hour"))
        .set("minute", moment(data[i].administeredDateTime).get("minute"))
        .set("second", moment(data[i].administeredDateTime).get("second"))
        .format("YYYY-MM-DD HH:mm:ss");

      finalAdministeredDateTime = moment(finalAdministeredDateTime)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss");
      // The modal emits `administration` ("administered" / "not administered"),
      // never `administeredFlag`. Testing the latter meant the guard was always
      // false, so an ADMINISTERED dose still took the "held" branch and would
      // carry a hold reason the moment one is wired up.
      const wasAdministered = data[i].administration == "administered";
      let requestObject = {
        id: data[i].id,
        administeredDateTime: finalAdministeredDateTime,
        administeredBy: data[i].electSignedBy,
        documentedDateTime: data[i].documentedDateTime,
        prescribedDateTime: this.utilityService.convertDateToServerFormat(
          `${formattedFilterDate} ${formattedCell}`
        ),
        administeredFlag: wasAdministered,
        reasoningForHolding: wasAdministered
          ? null
          : data[i].reasonForHolding == "Other"
          ? `other: ${data[i].other}`
          : data[i].reasonForHolding,
        prn: meds.prn,
        administeredNotes: data[i].administeredNotes,
        administeredNotes2: data[i].administeredNotes2,
        prescribedDosage: meds["medication"]["dosage"],
        administeredDosage:
          data[i].administration == "administered" ? data[i].dosage : "N/A",
        prescribedSlot: cell,
        actionTaken: data[i].actionTaken,
      };
      mar.push(requestObject);
      // mar["administeredDateTime"] = data["administeredDateTime"];
      // mar["documentedDateTime"] = data["documentedDateTime"];
      // let formattedFilterDate = moment(this.filterDate).format("YYYY-MM-DD");
      // let formattedCell = moment(cell, ["h:mm A"]).format("HH:mm");
      // mar["prescribedDateTime"] = this.utilityService.convertDateToServerFormat(`${formattedFilterDate} ${formattedCell}`) ;
      // mar["administeredFlag"] = data["administration"] == "administered" ? true : false;
      // mar["reasoningForHolding"] = mar["administeredFlag"] ? null :
      //     (data["reasonForHolding"] == "Other") ? `other: ${data["other"]}` : data["reasonForHolding"];
      // mar["prn"] = true;
      // mar["administeredNotes"] = data["administeredNotes"];
      // mar["prescribedDosage"] = meds["medication"]["dosage"];
      // mar["administeredDosage"] = mar["administeredFlag"] ? data["dosage"] : 'N/A';
      // mar["prescribedSlot"] = cell;
    }
    console.log(mar);

    this.dataService.increaseSpinnerCount();

    let marArray = [];

    for (let i = 0; i < mar.length; i++) {
      if (
        mar[i].actionTaken &&
        mar[i].actionTaken.toLowerCase() !== "created"
      ) {
        marArray.push(mar[i]);
      }
    }
    this.updateTimeSlot(meds["medication"]["id"], cell, marArray);

    this.marDataService
      .updateMAR(mar, this.patChartId, meds["medication"]["id"])
      .subscribe(
        (data: any) => {
          this.dataService.decreaseSpinnerCount();
          let createdMarArray = [];
          for (let i = 0; i < data.length; i++) {
            if (
              data[i].actionTaken &&
              data[i].actionTaken.toLowerCase() === "created"
            ) {
              createdMarArray.push(data[i]);
            }
          }
          this.updateTimeSlot(meds["medication"]["id"], cell, createdMarArray);
        },
        (err) => {
          this.dataService.decreaseSpinnerCount();
        }
      );
  }

  getFilterUTCDates() {
    let dates = {};
    let startDate = moment(this.filterDate)
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    let endDate = moment(this.filterDate)
      .endOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    dates["utcStartDate"] =
      this.utilityService.convertDateToServerFormat(startDate);
    dates["utcEndDate"] =
      this.utilityService.convertDateToServerFormat(endDate);
    dates["weekDay"] = moment(endDate, "YYYY-MM-DD HH:mm:ss").format("dddd");
    return dates;
  }

  ngOnDestroy() {
    if (this.getMARMesSubscription$) {
      this.getMARMesSubscription$.unsubscribe();
    }
  }

  presentMoreOptionsOnMarHeader($event) {
    let popover = this.popoverCtrl.create(
      EditViewPopoverPage,
      {
        showMarHeaderOptions: true,
        showPrintMarButton: true,
        isPatientChart: true,
      },
      { cssClass: "more-options-list-popover" }
    );
    popover.present({
      ev: $event,
    });
    popover.onDidDismiss((mode) => {
      if (mode && mode.print) {
        if (this.orderEntryToggle) {
          this.printMarReportV2();
        } else {
          this.printMarReport();
        }
      }
    });
  }

  printMarReportV2() {
    this.reportExecutionService.downloadReport(
      "MAR_report_v2",
      this.buildReportPayloadV2(),
      this.buildReportFilename("pdf"),
      "application/pdf"
    );
  }

  buildReportPayloadV2() {
    const orgId = this.authSvc.getUser().tenantId;
    const parameters = {
      pOrgID: orgId,
      pPatientChartID: this.patChartId,
      pReportDate: moment(this.filterDate).format("YYYY-MM-DD"),
      pTimeZone: this.getTimeZone(),
    };
    return {
      format: "pdf",
      parameters: parameters,
    };
  }

  printMarReport() {
    this.reportExecutionService.downloadReport(
      "MAR_report",
      this.buildReportPayload(),
      this.buildReportFilename("xls"),
      "application/vnd.ms-excel"
    );
  }

  buildReportPayload() {
    // const orgId = this.authSvc.getUserOrganization();
    const orgId = this.authSvc.getUser().tenantId;
    const parameters = {
      pOrgID: orgId,
      pPatientChartID: this.patChartId,
      pReportDate: moment(this.filterDate).format("YYYY-MM-DD"),
      pTimeZone: this.getTimeZone(),
    };
    return {
      format: "xls",
      parameters: parameters,
    };
  }

  getTimeZone() {
    let timeZone = moment.tz.guess();
    let time = new Date();
    return moment(time).tz(timeZone).format("z");
  }

  buildReportFilename(extension: string) {
    const orgName = this.authSvc.getUser().tenantName;
    let now = moment(new Date()).format("YYYY_MM_DD_HH_mm_ss");

    return `MAR_report_${orgName.replace(/ /g, "_")}_${now}.${extension}`;
  }

  getPrescribedTimeSlot(prescribed_slot: string): String {
    return this.timeslotLookup.get(prescribed_slot);
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/mar-card/mar-card.ts